/**
 * LogicSetDiagramReadOnly Styles
 * PBI 266671 - Logic Set dynamic palette information
 *
 * These styles mirror the canvas styles for visual consistency
 * while adding interactive highlighting for the modal.
 */

import styled from '@emotion/styled'
import { css } from '@emotion/core'

// Main wrapper for the diagram
export const DiagramWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
`

// Logic Set container - Figma specs
export const LogicSetContainer = styled.div`
  display: flex;
  width: 746px;
  height: 455px;
  padding: 25px 50px 25px 14px;
  margin-left: 41px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 16px;
  border: 1px solid #8c96a2;
  background: #fff;
  overflow: visible;
`
//   width: 711px;
//   padding: 66px 50px 25px 14px;
//
//   flex-direction: column;
//   align-items: flex-start;
//   gap: 10px;
//   border-radius: 16px;
//   border: 1px solid #8c96a2;
//   background: #fff;
//   overflow: visible;

// Logic Set header/title
export const LogicSetHeader = styled.div`
  color: rgba(7, 7, 15, 0.7);
  font-family: Arial;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  margin-left: 25px;
`

// Conditions column
export const ConditionsColumn = styled.div`
  width: 520px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
`

// Read-only condition box - Figma specs
// Top conditions (0,1): border-radius 0 16px 8px 0 (larger top-right corner)
// Bottom conditions (2,3): border-radius 0 8px 16px 0 (larger bottom-right corner)
export const ReadOnlyConditionBox = styled.div`
  position: relative;
  display: flex;
  width: 520px;
  height: 84px;
  padding-right: 20px;
  justify-content: space-between;
  align-items: center;
  border-radius: ${({ isBottomPair }) =>
    isBottomPair ? '0 8px 16px 0' : '0 16px 8px 0'};
  border: 4px solid #8c96a2;
  border-left: none;
  background: #e3e5e8;
  margin-top: ${({ isFirst }) => (isFirst ? '0' : '-4px')};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  box-sizing: border-box;

  /* Left color bar - green by default, changes to blue when selected */
  /* No border-radius on the pipe */
  &::before {
    content: '';
    width: 8px;
    flex-shrink: 0;
    position: absolute;
    top: -4px;
    bottom: -4px;
    left: 0;
    background: #47a817;
    transition: background-color 0.15s ease;
  }

  /* Hover styles - Figma specs (CSS hover or isHovered prop) */
  /* Note: Left pipe (::before) stays green on hover, only border changes to blue */
  &:hover {
    border-color: #1e7aff;
    background: #e3e5e8;
    z-index: 1;
  }

  ${({ isHovered }) =>
    isHovered &&
    css`
      border-color: #1e7aff;
      background: #e3e5e8;
      z-index: 1;
    `}

  /* Selected styles - Figma specs */
  ${({ isSelected }) =>
    isSelected &&
    css`
      background: #c1daff;
      border-color: #0051c4;
      z-index: 1;

      &::before {
        background: #0051c4 !important;
      }
    `}
`

// Content wrapper inside condition box
// Figma spec: text is 15px from the left edge of the pipe
export const ReadOnlyConditionContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px 23px;
  min-width: 0;
`

// Condition text - Figma specs
export const ReadOnlyConditionText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: rgba(7, 7, 15, 0.7);
  font-family: Arial;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  max-height: 64px;
  min-width: 0;
  overflow: hidden;
  flex: 1;
`

// Icons container for hysteresis and edge trigger
export const ReadOnlyConditionIcons = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;

  /* EdgeImg uses span > svg, not img */
  .edge-image {
    display: flex;
    align-items: center;
  }

  svg {
    display: block;
    width: 32px;
    height: 32px;
  }
`

// Hysteresis text - Figma specs
export const ReadOnlyHysteresisText = styled.span`
  font-family: Arial;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  color: rgba(7, 7, 15, 0.7);
  white-space: nowrap;
`

// Delay icons container - positioned in top right of LogicSetContainer
export const DelayIconsContainer = styled.div`
  display: flex;
  gap: 4px;
  position: absolute;
  top: 19px;
  right: 25px;
`

// Delay icon wrapper with 50px hit target - Figma specs
export const DelayIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 66px;
  cursor: pointer;
  transition: all 0.15s ease;
`

// Individual delay icon (AD/DD) - Legacy, kept for backwards compatibility
export const DelayIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.featureBorderColor || '#d9d9d9'};
  border-radius: 4px;
  background: ${({ theme }) => theme.color.whitePrimary};
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.paragraphColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.featureBorderHoveredColor || '#1890ff'};
    color: ${({ theme }) => theme.featureBorderHoveredColor || '#1890ff'};
  }

  ${({ isHovered, theme }) =>
    isHovered &&
    css`
      border-color: ${theme.featureBorderHoveredColor || '#1E7AFF'};
      color: ${theme.featureBorderHoveredColor || '#1E7AFF'};
    `}

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.featureBorderSelectedColor || '#0051C4'};
      background-color: ${theme.featureSubQueryBackgoundSelectedColor ||
      '#e6f7ff'};
      color: ${theme.featureBorderSelectedColor || '#0051C4'};
    `}
`

// Container for gates with custom connecting lines
export const GatesContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  min-width: 120px;
  height: 100%;
  overflow: visible;
`

// Individual gate wrapper - Figma specs
export const GateWrapper = styled.div`
  display: flex;
  width: 68px;
  height: 52px;
  justify-content: center;
  align-items: center;
`

// SVG container for custom connecting lines
export const ConnectingLinesSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
`
